import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ENTERPRISE_CONTRACT_FORMAT_OPTIONS,
  ENTERPRISE_CSM_CADENCE_OPTIONS,
  ENTERPRISE_ESTIMATED_TOOL_BUDGET_OPTIONS,
  ENTERPRISE_IMPLEMENTATION_TIMELINE_OPTIONS,
  ENTERPRISE_PLATFORM_MANAGEMENT_OPTIONS,
  ENTERPRISE_SECURITY_REQUIREMENTS_OPTIONS,
  ENTERPRISE_SUPPORT_LEVEL_OPTIONS,
  ENTERPRISE_WHITE_LABEL_OPTIONS,
} from "../../../utils/constants";
import {
  ENTERPRISE_INTAKE_FIELD_CONTRACT_FORMAT,
  ENTERPRISE_INTAKE_FIELD_CSM_CADENCE,
  ENTERPRISE_INTAKE_FIELD_ESTIMATED_TOOL_BUDGET,
  ENTERPRISE_INTAKE_FIELD_IMPLEMENTATION_TIMELINE,
  ENTERPRISE_INTAKE_FIELD_PLATFORM_MANAGEMENT,
  ENTERPRISE_INTAKE_FIELD_SECURITY_REQUIREMENTS,
  ENTERPRISE_INTAKE_FIELD_SUPPORT_LEVEL,
  ENTERPRISE_INTAKE_FIELD_URGENCY_DEADLINE,
  ENTERPRISE_INTAKE_FIELD_WHITE_LABEL,
  ENTERPRISE_INTAKE_HINT_ESTIMATED_TOOL_BUDGET,
  ENTERPRISE_INTAKE_HINT_IMPLEMENTATION_TIMELINE,
  ENTERPRISE_INTAKE_HINT_PLATFORM_MANAGEMENT,
  ENTERPRISE_INTAKE_HINT_WHITE_LABEL,
  ENTERPRISE_INTAKE_PLACEHOLDER_URGENCY_DEADLINE,
} from "../../../i18n/keysTranslations";
import FieldBlock from "../../PreActivation/components/FieldBlock";
import FieldLabel from "../../PreActivation/components/FieldLabel";
import IntakeTextField from "../../../components/Form/IntakeTextField";
import ReadOnlySectionGrid from "../../PreActivation/components/ReadOnlySectionGrid";
import CheckboxGroupWithOtherField from "./shared/CheckboxGroupWithOtherField";
import RadioFieldBlock from "./shared/RadioFieldBlock";
import RadioWithOtherField from "./shared/RadioWithOtherField";
import { mapTranslatedOptions } from "./shared/mapTranslatedOptions";

const SupportContractSection = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();

  const platformManagementOptions = useMemo(
    () =>
      mapTranslatedOptions({
        options: ENTERPRISE_PLATFORM_MANAGEMENT_OPTIONS,
        t,
      }),
    [t]
  );

  const supportLevelOptions = useMemo(
    () =>
      mapTranslatedOptions({ options: ENTERPRISE_SUPPORT_LEVEL_OPTIONS, t }),
    [t]
  );

  const csmCadenceOptions = useMemo(
    () => mapTranslatedOptions({ options: ENTERPRISE_CSM_CADENCE_OPTIONS, t }),
    [t]
  );

  const contractFormatOptions = useMemo(
    () =>
      mapTranslatedOptions({ options: ENTERPRISE_CONTRACT_FORMAT_OPTIONS, t }),
    [t]
  );

  const implementationTimelineOptions = useMemo(
    () =>
      mapTranslatedOptions({
        options: ENTERPRISE_IMPLEMENTATION_TIMELINE_OPTIONS,
        t,
      }),
    [t]
  );

  const whiteLabelOptions = useMemo(
    () => mapTranslatedOptions({ options: ENTERPRISE_WHITE_LABEL_OPTIONS, t }),
    [t]
  );

  const estimatedToolBudgetOptions = useMemo(
    () =>
      mapTranslatedOptions({
        options: ENTERPRISE_ESTIMATED_TOOL_BUDGET_OPTIONS,
        t,
      }),
    [t]
  );

  const securityOptions = useMemo(
    () =>
      mapTranslatedOptions({
        options: ENTERPRISE_SECURITY_REQUIREMENTS_OPTIONS,
        t,
      }),
    [t]
  );

  return (
    <ReadOnlySectionGrid readOnly={readOnly}>
      <RadioFieldBlock
        name="platformManagementModel"
        labelKey={ENTERPRISE_INTAKE_FIELD_PLATFORM_MANAGEMENT}
        hintKey={ENTERPRISE_INTAKE_HINT_PLATFORM_MANAGEMENT}
        required
        options={platformManagementOptions}
        control={control}
        readOnly={readOnly}
      />

      <RadioWithOtherField
        name="supportLevel"
        otherName="supportLevelOther"
        labelKey={ENTERPRISE_INTAKE_FIELD_SUPPORT_LEVEL}
        required
        options={supportLevelOptions}
        control={control}
        errors={errors}
        readOnly={readOnly}
      />

      <RadioWithOtherField
        name="csmCadence"
        otherName="csmCadenceOther"
        labelKey={ENTERPRISE_INTAKE_FIELD_CSM_CADENCE}
        required
        options={csmCadenceOptions}
        control={control}
        errors={errors}
        readOnly={readOnly}
      />

      <RadioWithOtherField
        name="contractFormat"
        otherName="contractFormatOther"
        labelKey={ENTERPRISE_INTAKE_FIELD_CONTRACT_FORMAT}
        required
        options={contractFormatOptions}
        control={control}
        errors={errors}
        readOnly={readOnly}
      />

      <RadioWithOtherField
        name="implementationTimeline"
        otherName="implementationTimelineOther"
        labelKey={ENTERPRISE_INTAKE_FIELD_IMPLEMENTATION_TIMELINE}
        hintKey={ENTERPRISE_INTAKE_HINT_IMPLEMENTATION_TIMELINE}
        required
        options={implementationTimelineOptions}
        control={control}
        errors={errors}
        readOnly={readOnly}
      />

      <RadioFieldBlock
        name="whiteLabelRequirement"
        labelKey={ENTERPRISE_INTAKE_FIELD_WHITE_LABEL}
        hintKey={ENTERPRISE_INTAKE_HINT_WHITE_LABEL}
        required
        options={whiteLabelOptions}
        control={control}
        readOnly={readOnly}
      />

      <FieldBlock>
        <FieldLabel
          htmlFor="urgencyDeadlineOrEvent"
          text={t(ENTERPRISE_INTAKE_FIELD_URGENCY_DEADLINE)}
        />
        <IntakeTextField
          name="urgencyDeadlineOrEvent"
          control={control}
          rules={{}}
          errors={errors}
          fullWidth
          readOnly={readOnly}
          placeholder={t(ENTERPRISE_INTAKE_PLACEHOLDER_URGENCY_DEADLINE)}
          variant="outlined"
        />
      </FieldBlock>

      <RadioFieldBlock
        name="estimatedToolBudgetRange"
        labelKey={ENTERPRISE_INTAKE_FIELD_ESTIMATED_TOOL_BUDGET}
        hintKey={ENTERPRISE_INTAKE_HINT_ESTIMATED_TOOL_BUDGET}
        required
        options={estimatedToolBudgetOptions}
        control={control}
        readOnly={readOnly}
      />

      <CheckboxGroupWithOtherField
        name="securityRequirements"
        otherName="securityRequirementsOther"
        labelKey={ENTERPRISE_INTAKE_FIELD_SECURITY_REQUIREMENTS}
        required
        options={securityOptions}
        control={control}
        errors={errors}
        readOnly={readOnly}
      />
    </ReadOnlySectionGrid>
  );
};

export default SupportContractSection;
